<template>
  <div class="cart-row">
    <button 
      @click="shoppingCartStore.removeProduct(product.id)" 
      class="delete" 
      :aria-label="$translate('REMOVE_PRODUCT_FROM_CART')"
      :tabindex="props.cartTabindex"
    >
      <TrashCanIcon />
    </button>

    <!-- Product image -->
    <div class="image">
      <a 
        :href="product.href"
        class="product-overlay"
        :tabindex="props.cartTabindex"
        :aria-label="product.name"
      >
        <Image
          :source="product.image?.image"
          :alt="product.image?.alt ? product.image.alt : product.name"
          :estimated-viewport-widths="{ small: 40, medium: 20, large: 20 }"
        />
      </a>
    </div>
    <div class="cart-info">
      <div class="product-info">
        
        <!-- Product title -->
        <div v-if="product.manufacturer_name" class="product-title">
          <a :href="product.href" :aria-label="product.name">{{ product.manufacturer_name }}</a>
        </div>
        <p>{{ product.name }}</p>

        <!-- Attributes-->
        <div class="attributes">
          <span v-for="(attribute, idx) in product.attributes" :key="idx">
            <span class="bold">{{ attribute.name }}:</span> {{ attribute.value }}
          </span>
        </div>
      </div>

      <!-- Quantity -->
      <div class="quantity">
        <button 
          @click="quantityInput.decreaseQuantity()"
          :aria-label="$translate('DECREASE_QUANTITY')"
          :tabindex="props.cartTabindex"
          type="button" 
        >
          <MinusIcon />
        </button>
        
        <label :for="`shopping-cart-${product.id}`" class="visually-hidden">
          {{ $translate('PRODUCT_QUANTITY_INPUT') }}
        </label>
        <ProductQuantityInput 
          v-model="quantity"
          :aria-label="$translate('PRODUCT_QUANTITY_INPUT')"
          ref="quantityInput"
          :hide-spin-buttons="true"
          @focus="handleInputFocus"
          @blur="handleInputBlur"
          :tabbable="props.cartTabindex"
          :labelId="`shopping-cart-${product.id}`"
          :lowest-number="product.product_data.lowest_number ? Number(product.product_data.lowest_number) : Number(product.product_data.package_size)"
          :package-size="Number(product.product_data.package_size)"
          :step="Number(product.product_data.package_size)"
        />
        
        <button
          @click="quantityInput.increaseQuantity()"
          :tabindex="props.cartTabindex"
          :aria-label="$translate('INCREASE_QUANTITY')"
          type="button"
        >
          <PlusIcon />
        </button>
      </div>

      <!-- Price -->
      <div class="price-wrapper">

        <!-- old price/logged price with logged price tooltip -->
        <div
          v-if="displayAsDiscounted && hasSpecialPrice" 
          :title="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')"
          class="price"
          :class="{ old: displayAsDiscounted }"
        >
          {{ formattedTotalPrice }}
          <tooltip
            :text="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')"
            class="info-icon"
          >
            <InfoIcon :aria-label="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')" />
          </tooltip> 
        </div>

        <!-- discounted by campaign with campaign tooltip -->
        <div
          v-if="hasCampaignDiscounts"
          :title="$translate('CART_CHECKOUT_CAMPAIGN_PRICE_TOOLTIP')"
          class="price old"
        >
          {{ hasSpecialPrice ? formattedSpecialPrice : formattedTotalPrice }}
          <tooltip
            :text="$translate('CART_CHECKOUT_CAMPAIGN_PRICE_TOOLTIP')"
            class="info-icon"
          >
            <InfoIcon :aria-label="$translate('CART_CHECKOUT_CAMPAIGN_PRICE_TOOLTIP')" />
          </tooltip> 
        </div>

        <!-- the actual final price -->
        <div 
          class="price"
          :class="{ new: displayAsDiscounted || hasCampaignDiscounts }"
        >
          {{ formattedFinalPrice }}
        </div>
      </div>
    </div>    
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { debounce } from '@/composables/debounce';
import { useShoppingCartStore } from '@/stores/shopping-cart.js';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';
import PlusIcon from '@/components/icons/PlusIcon.vue';
import MinusIcon from '@/components/icons/MinusIcon.vue';
import InfoIcon from '@/assets/icons/information-blue-rounded.svg?component';
import TrashCanIcon from '@/components/icons/TrashCanIcon.vue';
import Image from '@/components/Image.vue';
import ProductQuantityInput from '@/components/form/ProductQuantityInput.vue';

const shoppingCartStore = useShoppingCartStore();

const props = defineProps({
  product: {
    required: false,
  },
  cartTabindex: false,
  showStockStatus: {
    required: false,
    type: Boolean,
    default: false
  },
  currency: {
    type: String,
    required: false,
    default: 'SEK'
  }
});

const quantityInput = ref();
const cachedQuantity = ref();
const isFocused = ref(false);

const product = computed(() => {
  return props.product;
});

const quantity = ref(props.product.quantity);

watch(quantity, () => {
  if (!isFocused.value) {
    updateCart();
  }
});

const hasCampaignDiscounts = computed(() => {
  return product.value.discounts.length > 0;
});

const hasSpecialPrice = computed(() => {
  return !!product.value.product_data.price_special_raw;
});

const displayAsDiscounted = computed(() => {
  if (product.value.product_data.price_special_raw * product.value.quantity < product.value.total_price_raw) {
    return true;
  }
  return false;
});

const { formatPrice } = useCurrencyFormatter(props.currency);

const formattedTotalPrice = computed(() => {
  return formatPrice(product.value.total_price);
});

const formattedFinalPrice = computed(() => {
  return formatPrice(product.value.total_final_price);
});

const formattedSpecialPrice = computed(() => {
  if (product.value.product_data.price_special_raw) {
    const total = product.value.product_data.price_special_raw * product.value.quantity;
    return formatPrice(total);
  }
  return null;
});

const handleInputBlur = () => {
  isFocused.value = false;
  if (quantity.value !== cachedQuantity.value) {
    updateCart();
  }
}

const handleInputFocus = () => {
  isFocused.value = true;
  cachedQuantity.value = quantity.value;
}

const updateCart = () => {
  debounce(() => {
    const productInCart = shoppingCartStore.products.find(p => p.id === product.value.id);
    const diff = quantity.value - productInCart.quantity;
    
    const productData = {
      id: product.value.id,
      quantity_diff: diff,
    };

    shoppingCartStore.setProducts(productData);
  }, 500);
}
</script>
